import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { environment } from '../../../../../environments/environment';
import { currentUser } from '../../../../core/auth';
import { QueryParamsModel } from '../../../../core/_base/crud';
import { ActivatedRoute, Router } from '@angular/router';
import { DRPClassification, MedReviewConfig } from '../models';
import { isEqual, uniqWith } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class MedReviewService {
  private baseUrl = environment.MED_REVIEW_BASE;
  private drbUrl = environment.DRP_BASE;
  private devUrl = environment.USER_BASE;
  private companyId: any;

  private user: string;
  private tokenObject: any = {};
  private filter: any;
  private sort: any;
  private paginator: any;
  private updatePV1Warning = new Subject();
  private updateDRP = new Subject();
  private patientMedReviewFilter = new BehaviorSubject('');
  private medReviewIndex = -1;
  private medReviewChain: any | [] = [];
  currentMedReviewPatientId = 0;

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res.companyID;
      this.user = res.email;
    });
  }

  loadMedReviewData(queryParams: QueryParamsModel, patientId?: number): Observable<any> {
    // tslint:disable-next-line:one-variable-per-declaration
    const pageSize = queryParams.pageSize,
      pageIndex = queryParams.pageNumber;
    let params = new HttpParams()
      .set('page', String(pageIndex))
      .set('size', String(pageSize))
      .set('sort', `${queryParams.sortField},${queryParams.sortOrder}`);
    if (queryParams && queryParams.filter) {
      if (queryParams.filter.from_date) {
        params = params.set('cycle_date_after', queryParams.filter.from_date);
      }
      if (queryParams.filter.to_date) {
        params = params.set('cycle_date_before', queryParams.filter.to_date);
      }
      if (queryParams.filter.facility) {
        params = params.set('facility_name', queryParams.filter.facility.last_name);
      }
      if (queryParams.filter.patient) {
        params = params.set(
          'term',
          queryParams.filter.patient.first_name + ' ' + queryParams.filter.patient.last_name,
        );
      }
      if (queryParams.filter.office) {
        params = params.set('office_id', queryParams.filter.office.id);
      }
      if (queryParams.filter.badge) {
        params = params.set('indicator', queryParams.filter.badge);
      }
      if (queryParams.filter.status) {
        params = params.set('status', queryParams.filter.status);
      }
      if (queryParams.filter.doctor) params = params.set('doctor_id', queryParams.filter.doctor.id);
    }

    if (patientId) params = params.set('patient_id', patientId + '');

    // .set('sort', String(sortField)).set('order', String(sortOrder));
    const params1 = params.delete('page').delete('size').set('page', '0').set('size', '999999');
    this.httpClient
      .get(this.baseUrl + `/companies/${this.companyId}/medreviews/ids`, { params: params1 })
      .subscribe((res) => {
        this.medReviewChain = res;
        localStorage.setItem('medReviews', JSON.stringify(this.medReviewChain));
      });
    return this.httpClient.get(this.baseUrl + `/companies/${this.companyId}/medreviews`, {
      params,
    });
  }
  // triggerType: string,
  loadMedreviewBmabooHealthData( triggerType: string, medReviewId) {
    return this.httpClient.post(
      this.baseUrl + `/companies/${this.companyId}/medreviews/${medReviewId}/bamboo-health-check?triggerType=${triggerType}`,
      {},
    );
    console.log('medReviewId:', medReviewId);
  }
  getMedReviewsFromLocalStorage() {
    if (localStorage.getItem('medReviews'))
      this.medReviewChain = JSON.parse(localStorage.getItem('medReviews'));
  }
  getIndicators(indicators: any) {
    let div = `<div class="">`;
    if (indicators && indicators.length) {
      if (typeof indicators === 'string') indicators = indicators.split(',');
      indicators.forEach((e) => {
        e = e.trim();
        let innerDivs = `<div class="indicator `;
        innerDivs += this.getIndicatorsClass(e);
        innerDivs += `">`;
        innerDivs += e;
        innerDivs += `</div>`;
        div += innerDivs;
      });
    }
    div += `</div>`;
    return div;
  }

  private getIndicatorsClass(e: any) {
    switch (e) {
      case 'NTS':
        return 'indicator-blue';
      case 'RES':
        return 'indicator-A3E4DB';
      case 'CHG':
      case 'HOA':
        return 'indicator-orange';
      case 'ADM':
      case 'DOSE':
        return 'indicator-green';
      case 'READM':
      case 'FREQ':
      case 'REC':
        return 'indicator-grey';
    }
  }

  getPatientMedReview(medReviewId) {
    return this.httpClient.get(
      this.baseUrl + `/companies/${this.companyId}/medreviews/${medReviewId}`,
    );
  }

  getMedReviewByPatientId(patientId: number) {
    return this.httpClient.get(
      `${this.baseUrl}/companies/${this.companyId}/medreviews?patient_id=${patientId}`,
    );
  }

  completeMedReview(id): Observable<any> {
    return this.httpClient.put(this.baseUrl + `/companies/${this.companyId}/medreviews/${id}`, {
      status: 'DONE',
    });
  }

  moveMedicationToDiscontinued(id, body) {
    return this.httpClient.post(
      this.baseUrl + `/companies/${this.companyId}/medications/${id}/discontinue`,
      body,
    );
  }

  verifyMedications(map: any[]) {
    return this.httpClient.put(
      this.baseUrl + `/companies/${this.companyId}/medications/verify`,
      map,
    );
  }

  verifyMedicationOnLocalNetwork(office, rxno) {
    const headers = new HttpHeaders().set(
      'Authorization',
      `${this.tokenObject.token_type} ${this.tokenObject.access_token}`,
    );
    return this.httpClient.get(`https://api.medplus.co/rxpv/v1/${office}/${rxno}/${this.user}`, {
      headers,
    });
  }

  markErrorOnMedications(medication, errorCode, errorNote): Observable<any> {
    return this.httpClient.post(
      this.baseUrl +
        `/companies/${this.companyId}/medications/${medication.id}/error?office=${
          medication.prescription.office_id || 0
        }&rxno=${medication.rxno}`,
      { error_code: errorCode, error_note: errorNote },
    );
  }

  getMedScore(patient_ids): Observable<any> {
    return this.httpClient.post(
      this.baseUrl + `/companies/${this.companyId}/medreviews/mrs/score`,
      patient_ids,
    );
  }

  setCurrentFilter(filter: any) {
    this.filter = filter;
  }

  getCurrentFilter() {
    return this.filter;
  }

  setCurrentSort(sort) {
    this.sort = sort;
  }

  setCurrentPage(paginator) {
    this.paginator = paginator;
  }

  getCurrentSort() {
    return this.sort;
  }

  getCurrentPage() {
    return this.paginator;
  }

  getUpdatePV1Warning() {
    return this.updatePV1Warning.asObservable();
  }
  getUpdateDRP() {
    return this.updateDRP.asObservable();
  }

  emitPV1Change() {
    this.updatePV1Warning.next(Math.random());
  }
  emitDRP() {
    this.updateDRP.next(Math.random());
  }

  updatePatientMedReviewFilter(value: string) {
    this.patientMedReviewFilter.next(value);
  }

  getPatientMedReviewFilter(): Observable<any> {
    return this.patientMedReviewFilter.asObservable();
  }

  removeMedReviewFromChain() {
    this.medReviewChain.splice(this.medReviewIndex, 1);
    localStorage.setItem('medReviews', JSON.stringify(this.medReviewChain));
    this.medReviewIndex = this.medReviewIndex % this.medReviewChain.length;
    this.router.navigate(['/med-review/board', this.medReviewChain[this.medReviewIndex]], {});
  }

  updateMedReviewIndex(id) {
    this.medReviewIndex = this.medReviewChain.indexOf(1 * id);
  }

  nextMedReview() {
    this.medReviewIndex = (this.medReviewIndex + 1) % this.medReviewChain.length;
    this.navigateToMedReview();
  }

  previousMedReview() {
    this.medReviewIndex = this.medReviewIndex - 1;
    if (this.medReviewIndex < 0) {
      this.medReviewIndex = this.medReviewChain.length - 1;
    }
    this.navigateToMedReview();
  }

  navigateToMedReview() {
    this.router.navigate(['/med-review/board', this.medReviewChain[this.medReviewIndex]], {});
  }

  getMedReviewIndex() {
    return this.medReviewIndex;
  }

  getMedicationHistory(id) {
    return this.httpClient.get(
      this.baseUrl + `/companies/${this.companyId}/medications/${id}/history`,
    );
  }

  getMedicationHistoryByPatientAndHicl(patientId, hicl) {
    return this.httpClient.get(
      this.baseUrl +
        `/companies/${this.companyId}/medications/history?patient_id=${patientId}&hicl=${hicl}`,
    );
  }

  getDrps(patient_id, status) {
    return this.httpClient.get<any>(
      this.drbUrl + `/companies/${this.companyId}/patients/${patient_id}/drps?status=${status}`,
    );
  }

  getDrpCount(patient_id) {
    return this.httpClient.get(
      this.drbUrl + `/companies/${this.companyId}/patients/${patient_id}/drps/counts`,
    );
  }

  findInterventionHistory(queryParams: QueryParamsModel, patient_id, drp_id): Observable<any> {
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const params = new HttpParams()
      .set('page', String(pageIndex))
      .set('size', String(pageSize))
      .set('sort', `${queryParams.sortField},${queryParams.sortOrder}`);
    return this.httpClient.get(
      this.drbUrl +
        `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/interventions/history`,
      { params },
    );
  }

  pendOrIgnoreDRP(body, patient_id, drp_id) {
    return this.httpClient.put(
      this.drbUrl + `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/pend`,
      body,
    );
  }

  getReasonsSelect(): Observable<any> {
    return this.httpClient.get<any>(
      this.drbUrl + `/companies/${this.companyId}/reasons/select-all`,
    );
  }

  getInterventionLevelsSelect(): Observable<any> {
    return this.httpClient.get<any>(this.drbUrl + `/lists/levels`);
  }

  getClassificationCategories(): Observable<any> {
    return this.httpClient.get<any>(this.drbUrl + `/lists/classification_categories`);
  }

  getOutcomesAutocomplete(search_term: string): Observable<any> {
    const url =
      this.drbUrl + '/lists/outcome_codes/autocomplete?sort=outcome,asc&term=' + search_term;
    return this.httpClient.get<any>(url);
  }

  getAcceptanceAutocomplete(search_term: string): Observable<any> {
    const url =
      this.drbUrl +
      '/lists/acceptance_codes/autocomplete?sort=implementation,asc&term=' +
      search_term;
    return this.httpClient.get<any>(url);
  }

  getInterventionCodesAutocomplete(level, search_term: string): Observable<any> {
    const url =
      this.drbUrl +
      '/lists/intervention_codes/autocomplete?sort=description,asc&term=' +
      search_term +
      '&level=' +
      level;
    return this.httpClient.get<any>(url);
  }

  getTeamAutocomplete(search_term: string): Observable<any> {
    const url =
      this.devUrl +
      `/companies/${this.companyId}/category/autocomplete?has_intervention_permission=true&term=` +
      search_term;
    return this.httpClient.get<any>(url);
  }

  getCauseAutocomplete(problem_code, search_term: string): Observable<any> {
    const url =
      this.drbUrl +
      `/lists/cause_codes/autocomplete?sort=description,asc&problem_code=${problem_code}&term=` +
      search_term;
    return this.httpClient.get<any>(url);
  }

  getDrugByNDC(codes): Observable<any> {
    let codesArr = '';
    codes = uniqWith(codes, isEqual);
    codes.forEach((element) => (codesArr = codesArr + '&code=' + element));
    const url = environment.CORE_BASE + `/companies/${this.companyId}/drugs/get-by-codes`;
    const params = new HttpParams({ fromString: codesArr });
    return this.httpClient.get<any>(url, { params });
  }

  getPrescriptionByPatientAndDrug(patient_id, drug_ids): Observable<any> {
    let drugs = '';
    drug_ids = uniqWith(drug_ids, isEqual);
    drug_ids.forEach((element) => (drugs = drugs + '&drug_id=' + element));
    const params = new HttpParams({ fromString: drugs });
    const url =
      environment.CORE_BASE +
      `/companies/${this.companyId}/patients/${patient_id}/prescriptions/get-by-patient-drug`;
    return this.httpClient.get<any>(url, { params });
  }

  manageDRP(body, patient_id, drp_id) {
    return this.httpClient.post<any>(
      this.drbUrl +
        `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/interventions`,
      body,
    );
  }

  createCustomDRP(body, patient_id) {
    return this.httpClient.post<any>(
      this.drbUrl + `/companies/${this.companyId}/patients/${patient_id}/drps`,
      body,
    );
  }

  mapInterventionToTask(body, patient_id, drp_id) {
    return this.httpClient.put(
      this.drbUrl +
        `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/interventions/map-to-task`,
      body,
    );
  }

  createTaskIntervention(tasks) {
    return this.httpClient.post<any>(
      environment.TIMELINE_BASE + `/companies/${this.companyId}/tasks_v2`,
      tasks,
    );
  }

  getDefaultTeam() {
    return this.httpClient.get<any>(
      environment.USER_BASE + `/companies/${this.companyId}/category/check-default`,
    );
  }

  getInterventionByTaskId(patient_id, drp_id, task_id) {
    const url =
      this.drbUrl +
      `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/interventions/get-by-task-id?task_id=${task_id}`;
    return this.httpClient.get<any>(url);
  }
  updateDRPIntervention(body, patient_id, drp_id, intervention_id) {
    return this.httpClient.put(
      this.drbUrl +
        `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}/interventions/${intervention_id}`,
      body,
    );
  }
  updateOrResolveDRP(body, patient_id, drp_id) {
    return this.httpClient.put(
      this.drbUrl + `/companies/${this.companyId}/patients/${patient_id}/drps/${drp_id}`,
      body,
    );
  }

  searchDRPClassification(term = ''): Observable<DRPClassification> {
    return this.httpClient.get<DRPClassification>(
      `${this.drbUrl}/lists/drp_classifications/autocomplete?sort=problem,asc&term=${term}`,
    );
  }

  getMedReviewConfigs(): Observable<MedReviewConfig[]> {
    return this.httpClient.get<MedReviewConfig[]>(
      `${this.baseUrl}/companies/${this.companyId}/medreviews/configs`,
    );
  }

  updateMedReviewConfigs(configs: MedReviewConfig[]): Observable<HttpResponse<void>> {
    return this.httpClient.put<void>(
      `${this.baseUrl}/companies/${this.companyId}/medreviews/configs`,
      { configs },
      { observe: 'response' },
    );
  }
}
